import { FlagsProvider } from '@innovamat/flags';
import {
  EnvVariablesProvider,
  UserManagementProvider,
} from '@innovamat/ga-features';
import { ThemeProvider } from '@innovamat/glimmer-components';
import {
  GlowApiClientProvider,
  usePasswordExpirationStatusQuery,
} from '@innovamat/glow-api-client';
import type { Credentials } from '@innovamat/social-login';
import { AuthProvider } from '@innovamat/social-login';
import { WebviewBridgeProvider } from '@innovamat/webview-bridge';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { initEnvVariables } from '../../utils/init-env-variables';
import {
  getAccessToken,
  getUserAcceptLanguage,
  updateRefreshTokenClient,
} from '../../utils/update-refresh-token';

import { EventLoggingProvider } from '@innovamat/event-logging';
import { storage } from '@innovamat/radiance-utils';
import { useRegionalConfigVariables } from '@innovamat/regional-config-variables';
import { PreprTrackingProvider } from '@innovamat/smart-guides';
import { useEffect, useState } from 'react';
import { authServiceInit } from '../../config/auth-service-init';
import { useRedirectWithReferral } from '../../utils/redirect-with-referral';
import {
  CLIENT_ID,
  CONFIG_CAT_CACHE_TIME,
  CONFIG_CAT_KEY,
  ENVIRONMENT,
  GOOGLE_CLIENT_ID,
  LAMBDA_API_URL,
  ROSTER_STREAM_APP_ID,
} from '../../utils/variables';
import { ErrorBoundary } from './error-boundary';

const initialCredentials = {
  apolloUrl: '',
  usersClientId: CLIENT_ID,
  apiUsersUrl: '',
  rosterStreamAppId: ROSTER_STREAM_APP_ID,
  googleClientId: GOOGLE_CLIENT_ID,
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

type Props = {
  children: React.ReactNode;
};

export function Providers({ children }: Props): JSX.Element {
  const regionalConfigVariables = useRegionalConfigVariables();
  const { onRedirect } = useRedirectWithReferral();
  authServiceInit(regionalConfigVariables.apiUsers);

  const [credentials, setCredentials] =
    useState<Credentials>(initialCredentials);

  useEffect(() => {
    setCredentials((prevCredentials) => ({
      ...prevCredentials,
      apolloUrl: regionalConfigVariables.apolloServer,
      apiUsersUrl: regionalConfigVariables.apiUsers,
    }));
  }, [regionalConfigVariables]);

  const handleRefreshToken = async (): Promise<void> => {
    await updateRefreshTokenClient();
    //Task: https://innovamat.atlassian.net/browse/GAM-5218
    //We invalidate the password status query every time there's a refresh token in order to keep its value up to date in localStorage.
    queryClient.fetchQuery({
      queryKey: usePasswordExpirationStatusQuery.getKey(),
    });
  };

  if (credentials.apolloUrl === '' || credentials.apiUsersUrl === '') {
    return <></>;
  }

  return (
    <ThemeProvider type="light">
      <ErrorBoundary>
        <EnvVariablesProvider
          envVariables={initEnvVariables(regionalConfigVariables)}
        >
          <GlowApiClientProvider
            client={queryClient}
            apiUrl={regionalConfigVariables.apolloServer}
            getAuthToken={() => getAccessToken()}
            getUserAcceptLanguage={() => getUserAcceptLanguage()}
            onRefreshToken={handleRefreshToken}
            onRequestError={(error) => {
              if (error === 'UNAUTHENTICATED') {
                storage.clear();
                onRedirect();
              }
            }}
          >
            <AuthProvider credentials={credentials}>
              <EventLoggingProvider
                variables={{
                  // Solo variables necesarias - todas las API keys ahora están en Lambda
                  LAMBDA_API_URL,
                  ENVIRONMENT,
                }}
              >
                <UserManagementProvider>
                  <WebviewBridgeProvider>
                    <FlagsProvider
                      sdkKey={CONFIG_CAT_KEY}
                      cacheTime={CONFIG_CAT_CACHE_TIME}
                    >
                      <PreprTrackingProvider>{children}</PreprTrackingProvider>
                      <ReactQueryDevtools initialIsOpen={false} />
                    </FlagsProvider>
                  </WebviewBridgeProvider>
                </UserManagementProvider>
              </EventLoggingProvider>
            </AuthProvider>
          </GlowApiClientProvider>
        </EnvVariablesProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
